// ────────────────────────────────────────────────────────────────────────────
// `DocumentData` — the single bag of values every template binds to.
//
// This is the contract between "what the app knows" and "what a template can
// print". Templates never reach into an `Invoice` DTO directly: the renderer
// and the token picker both read this shape, so adding a printable value is
// one field here plus one entry in the relevant registry — and a template
// written today keeps working when the underlying DTO changes.
//
// Invoices and credit notes share the shape; `doc.typeName` carries the word
// that gets printed ("INVOICE" / "CREDIT NOTE") and amounts stay positive for
// both (a credit note is identified by its title and number series, not by
// negative figures).
// ────────────────────────────────────────────────────────────────────────────
import type { Invoice } from '@my/tms-api';
import type { DocumentType } from './types';

export interface DocumentOrganisation {
  name: string;
  addressLines: string[];
  taxNumber?: string | null;
  registrationNumber?: string | null;
  phone?: string | null;
  email?: string | null;
  website?: string | null;
  /** Absolute URL of the org logo blob; omitted when the org has none. */
  logoUrl?: string | null;
}

export interface DocumentParty {
  name: string;
  code?: string | null;
  /**
   * The address exactly as stored — one Google-formatted, comma-separated
   * string. Unlike the organisation block (whose lines the operator edits by
   * hand), this is broken up at print time by the band's `addressFormat`,
   * because the customer changes on every document.
   */
  address?: string | null;
  taxNumber?: string | null;
  contactName?: string | null;
  phone?: string | null;
  email?: string | null;
}

export interface DocumentHeaderData {
  /** Printed document word — drives the default title token. */
  typeName: string;
  docType: DocumentType;
  number: string;
  issueDate?: string | null;
  dueDate?: string | null;
  poNumber?: string | null;
  reference?: string | null;
  paymentTerms?: string | null;
  currencyIsoCode: string;
  /**
   * A credit note's reference to what it credits —
   * "Credit note for invoice INV-000040 dated 01 Sep 2026" — and why. A credit
   * note must identify the invoice it corrects; printed above the lines.
   */
  creditNoteFor?: string | null;
  creditReason?: string | null;
}

/**
 * Order-derived context for a line — the transport detail an invoice prints
 * alongside the charge.
 *
 * **This is the RENDERER's input contract, not the storage shape.** The two are
 * deliberately different sizes:
 *
 *  * `InvoiceLine` stays light — it already holds `OrderId`, and gains the
 *    line's date plus the `OrderTaskId`(s) it was billed from. The composed
 *    route text goes into `Description`, so no address is stored twice.
 *  * Generation resolves those ids through the order graph (`OrderTask` dates,
 *    `OrderTaskResource` trailers, `OrderTaskCargoItem` containers and product,
 *    `Order.Reference1..4`) and fills this object in to render the PDF.
 *
 * That split works because the issued PDF is the record: the resolved values
 * are frozen into the stored blob, so nothing has to be duplicated onto the
 * line to keep a reprint honest. The cost is that *regenerating* a document
 * later re-resolves through the ids and can pick up edits made to the order
 * since — fine when regenerating is a deliberate act, which is the intent.
 *
 * Both ends of the journey are still modelled here because the *layout*
 * chooses which to show; generation supplies whichever the template asks for.
 */
export interface DocumentLineContext {
  /**
   * The order tasks this line was billed from. Stored on the invoice line;
   * everything below is resolved from them at generation rather than duplicated.
   */
  orderTaskIds?: string[];
  collectionDate?: string | null;
  deliveryDate?: string | null;
  /**
   * Short place names — city or address name, not the full address. Resolved
   * from the tasks at generation; on a line whose route is already composed
   * into its description these are absent and the `details` column falls back
   * to that description.
   */
  collectionPlace?: string | null;
  deliveryPlace?: string | null;
  /**
   * The order's reference slots, positionally: index 0 = `Reference1`. Read
   * through the line's `OrderId` at generation — not snapshotted, since the
   * order id is already on the line. The organisation names these slots
   * (`tmsOrderReferenceLabelsJson`), so the column picks a slot and takes its
   * label from the org.
   */
  references?: (string | null)[];
  trailer?: string | null;
  container?: string | null;
  cargoProduct?: string | null;
  cargoTemperatureC?: number | null;
}

export interface DocumentLine {
  id: string;
  description: string;
  /** Soft ref to the originating order — the key context is matched on. */
  orderId?: string | null;
  orderNumber?: string | null;
  reference?: string | null;
  /**
   * The order's context, attached to EVERY line of that order. Which cells
   * actually print is decided by `markContinuations` — the first line of an
   * order shows the date / order number / reference and the rest leave them
   * blank — so the data being present on each line costs nothing and means any
   * line can be the one that prints it.
   */
  context?: DocumentLineContext;

  /**
   * True for the order's transport charge.
   *
   * The route ("Spalding – Dublin") describes the *carriage*, so only this line
   * may print it in place of its own description. A fuel surcharge or waiting
   * time is not a journey, and showing the route on one — which is what
   * happened when the route went to whichever line came first — reads as though
   * the surcharge were the transport itself. Exclude the transport charge from
   * an invoice and no line claims the route, while the date and order reference
   * still print because they belong to the order rather than the charge.
   */
  isTransportCharge?: boolean;
  /**
   * Quantity / unit price are optional because TMS invoice lines are charge
   * amounts, not priced units — orgs that want these columns will need the
   * charge model to carry them. Blank renders as an em dash rather than 0.
   */
  quantity?: number | null;
  unitPrice?: number | null;
  net: number;
  taxRatePct: number;
  taxAmount: number;
  gross: number;
  /** The goods carried, as snapshotted on the line ("Sulphuric Acid (UN 1830)"). */
  products?: string | null;
  /** The tax schema the line was raised with — bands are per schema, not per rate. */
  taxRateId?: string | null;
  taxRateName?: string | null;
  taxType?: string | null;
  /** The schema's "Description on invoice". */
  taxDescription?: string | null;
}

export interface DocumentVatBand {
  /** The schema id, or `pct:<rate>` for lines raised before schemas were kept. */
  key: string;
  ratePct: number;
  net: number;
  vat: number;
  /**
   * What the band says instead of a bare "0%": the schema's "Description on
   * invoice" (else its name) — "Reverse charge — customer to account for
   * VAT". Null for a band with no schema behind it.
   */
  label?: string | null;
  taxType?: string | null;
}

/**
 * Treatments whose wording the document must print even with the VAT
 * breakdown switched off: a 0% that is not "no VAT due here because it is
 * 0%" but reverse charge, outside scope, zero-rated or exempt — each a
 * statement the invoice has to make.
 */
export const WORDED_TAX_TYPES = new Set(['ReverseCharge', 'NoVat', 'Zero', 'Exempt', 'IntraEu']);

export interface DocumentTotals {
  subtotal: number;
  vatTotal: number;
  total: number;
  /** One entry per distinct VAT rate on the document. */
  vatBands: DocumentVatBand[];
}

export interface DocumentBank {
  accountName?: string | null;
  bankName?: string | null;
  iban?: string | null;
  bic?: string | null;
  sortCode?: string | null;
  accountNumber?: string | null;
}

export interface DocumentData {
  organisation: DocumentOrganisation;
  customer: DocumentParty;
  doc: DocumentHeaderData;
  lines: DocumentLine[];
  totals: DocumentTotals;
  bank?: DocumentBank;
  /** Free-text message captured on the invoice itself. */
  message?: string | null;
  /**
   * A reverse-charge line is on the document, so the customer's VAT number
   * must print whether or not the layout's customer block lists it.
   */
  customerVatNumberRequired?: boolean;
}

/** The word printed at the top of each document kind. */
export const DOC_TYPE_PRINT_NAME: Record<DocumentType, string> = {
  Invoice: 'Invoice',
  CreditNote: 'Credit note',
  Proforma: 'Pro forma invoice',
};

/**
 * Sum lines into VAT bands — one per TAX SCHEMA, not per rate: reverse charge,
 * zero-rated and outside scope are all 0% and must not merge, each carrying its
 * own wording. Lines raised before schemas were kept band by rate as before.
 * Ascending rate, then label.
 */
export function buildVatBands(lines: DocumentLine[]): DocumentVatBand[] {
  const byKey = new Map<string, DocumentVatBand>();
  for (const line of lines) {
    const key = line.taxRateId || `pct:${line.taxRatePct}`;
    const band = byKey.get(key);
    if (band) {
      band.net += line.net;
      band.vat += line.taxAmount;
    } else {
      byKey.set(key, {
        key,
        ratePct: line.taxRatePct,
        net: line.net,
        vat: line.taxAmount,
        label: line.taxDescription?.trim() || line.taxRateName?.trim() || null,
        taxType: line.taxType ?? null,
      });
    }
  }
  return [...byKey.values()].sort(
    (a, b) => a.ratePct - b.ratePct || (a.label ?? '').localeCompare(b.label ?? ''),
  );
}

/** "01 Sep 2026" — how a referenced document's date prints. */
function printDate(iso: string): string {
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('en-IE', { day: '2-digit', month: 'short', year: 'numeric' });
}

/** Split a stored one-line address into printable lines. */
export function splitAddress(address: string | null | undefined): string[] {
  if (!address) return [];
  return address
    .split(/\r?\n|,/)
    .map((part) => part.trim())
    .filter(Boolean);
}

/**
 * Adapt a persisted invoice into `DocumentData`. Everything customer-facing
 * comes from the invoice's own snapshot (never a fresh Directory lookup) so a
 * reprint years later matches the document that was originally issued; the
 * organisation block is the only part read live — that is the org doing the work.
 */
export function invoiceToDocumentData(
  invoice: Invoice,
  organisation: DocumentOrganisation,
  bank?: DocumentBank,
): DocumentData {
  const docType: DocumentType = invoice.docType === 'Credit' ? 'CreditNote' : 'Invoice';
  const lines: DocumentLine[] = invoice.lines
    .slice()
    .sort((a, b) => a.sequence - b.sequence)
    .map((l) => ({
      id: l.id,
      description: l.description,
      orderId: l.orderId,
      orderNumber: l.orderNumber,
      isTransportCharge: l.type === 'TransportCharge',
      reference: null,
      quantity: null,
      unitPrice: null,
      net: l.net,
      taxRatePct: l.taxRatePct,
      taxAmount: l.taxAmount,
      gross: l.net + l.taxAmount,
      products: l.products ?? null,
      taxRateId: l.taxRateId ?? null,
      taxRateName: l.taxRateName ?? null,
      taxType: l.taxType ?? null,
      taxDescription: l.taxDescription ?? null,
    }));

  return {
    organisation,
    customer: {
      name: invoice.billingName || invoice.customerName,
      code: invoice.customerCode,
      // Kept whole: the snapshot is the Address record's `fullAddress`, and the
      // layout decides how to break it.
      address: invoice.billingAddress,
      // Snapshotted at generation, like the address.
      taxNumber: invoice.customerTaxNumber ?? null,
    },
    doc: {
      typeName: DOC_TYPE_PRINT_NAME[docType],
      docType,
      number: invoice.number,
      issueDate: invoice.issueDate,
      dueDate: invoice.dueDate,
      poNumber: invoice.poNumber,
      // Snapshotted on the invoice at generation from the customer's terms (or
      // the organisation's default) — never recomputed here, so a reprint shows
      // the terms that were actually agreed at the time.
      paymentTerms: invoice.paymentTerms,
      currencyIsoCode: invoice.currencyIsoCode || 'EUR',
      creditNoteFor: invoice.creditNoteOfNumber
        ? `Credit note for invoice ${invoice.creditNoteOfNumber}` +
          (invoice.creditNoteOfIssueDate ? ` dated ${printDate(invoice.creditNoteOfIssueDate)}` : '')
        : null,
      creditReason: invoice.creditReason ?? null,
    },
    lines,
    totals: {
      subtotal: invoice.subtotal,
      vatTotal: invoice.vatTotal,
      total: invoice.total,
      vatBands: buildVatBands(lines),
    },
    bank,
    message: invoice.message,
    customerVatNumberRequired: lines.some((l) => l.taxType === 'ReverseCharge'),
  };
}
